import React, { useRef } from 'react';
import { useLocation } from 'react-router';

// Types
import { GoalItem } from 'Shared/constants/types';
import { GoalTableColumns } from '../Goals/GoalsTable/GoalsTable';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';
import { useGoalDetailPanel } from 'Shared/utils/hooks/use-goal-detail-panel';
import { useToggle } from 'Shared/utils/hooks';

// Components
import GoalName from '../Goals/GoalName/GoalName';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import { WpIcon } from 'Wpds/components/icon';
import GoalStatus from '../Goals/GoalStatus/GoalStatus';
import GoalOwner from '..//Goals/GoalOwner/GoalOwner';
import GoalDueDate from '../Goals/GoalDueDate/GoalDueDate';
import GoalDetailsRelatedGoals from '../Goals/GoalDetailsRelatedGoals/GoalDetailsRelatedGoals';
import GoalCompleteButton from '../Goals/GoalCompleteButton/GoalCompleteButton';
import GoalCompleteAlert from '../Goals/GoalCompleteAlert/GoalCompleteAlert';
import GoalDescription from '../Goals/GoalDescription/GoalDescription';
import GoalCommentInput from '../Goals/GoalCommentInput/GoalCommentInput';
import GoalComments from '../Goals/GoalComments/GoalComments';
import GoalProgress from '../Goals/GoalProgress/GoalProgress';
import GoalCustomTableRow from '../Goals/GoalCustomTableRow/GoalCustomTableRow';
import GoalActionItems from '../Goals/GoalActionItems/GoalActionItems';

// Styles
import './GoalDetailPanelContent.scss';
import { WpCollapsible } from 'Wpds/components/collapsible';

const GoalDetailPanelContent = () => {
  const scrollToRef = useRef(null);
  const { handleOpenPanel } = useGoalDetailPanel();

  const { pathname } = useLocation();
  const slug = pathname.match(/\/goals\/(.*)\/detail/)[1];
  const { goal, isComplete, isProgressActionItems, actionItems } = useGoal({ slug });

  const [isActionItemsOpen, toggleIsActionItemsOpen] = useToggle(isProgressActionItems);
  const [isSubGoalsOpen, toggleIsSubGoalsOpen] = useToggle(!isProgressActionItems);

  const handleRowClickOfRelatedGoals = (goal: GoalItem) => {
    handleOpenPanel(goal.slug);
  };

  const scrollToCallBack = () => {
    scrollToRef.current?.scrollIntoView();
  };

  if (!goal) return null;

  const baseClassName = 'GoalDetailPanelContent';

  const actionItemsJsx = (
    <div className={`${baseClassName}__section`}>
      <WpButton size="sm" variant="tertiary" color="gray" onClick={toggleIsActionItemsOpen} square slimPadding>
        {actionItems.length > 0 ? actionItems.length : ''} Action Items
        <WpIcon name={isActionItemsOpen ? 'angle-down' : 'angle-right'} />
      </WpButton>
      <WpCollapsible open={isActionItemsOpen}>
        <div className={`${baseClassName}__section__collapsible-content`}>
          <GoalActionItems goal={goal} showProgress={false} forceShowMobileActions showTableHeaders={false} />
        </div>
      </WpCollapsible>
    </div>
  );

  return (
    <div className={baseClassName}>
      {!goal ? (
        <>loading</>
      ) : (
        <>
          <div className={`${baseClassName}__body`}>
            <div className={`${baseClassName}__body__content`}>
              <GoalName goal={goal} showLinkedIndicator={false} editable />
              <div className={`${baseClassName}__complete-button`}>
                <WpButtonGroup>
                  <GoalCompleteButton goal={goal} size="sm" color="gray" />
                  <GoalStatus goal={goal} showCompleteIndicator={false} editable />
                </WpButtonGroup>
              </div>
              {/* {canChangeStatus && (
                <div className={`${baseClassName}__complete-button`}>
                  <GoalCompleteButton goal={goal} size="sm" color="gray" />
                </div>
              )} */}
              {isComplete && (
                <div className={`${baseClassName}__complete-alert`}>
                  <GoalCompleteAlert goal={goal} />
                </div>
              )}
              {goal.description && (
                <div className={`${baseClassName}__description`}>
                  <GoalDescription goal={goal} editable />
                </div>
              )}
              <div className={`${baseClassName}__details`}>
                <div className={`${baseClassName}__details__item`}>
                  <p className={`${baseClassName}__details__item__title`}>Due</p>
                  <GoalDueDate goal={goal} editable />
                </div>
                <div className={`${baseClassName}__details__item`}>
                  <p className={`${baseClassName}__details__item__title`}>Owner</p>
                  <GoalOwner goal={goal} editable />
                </div>
              </div>
              <div className={`${baseClassName}__section`}>
                <GoalProgress goal={goal} inContainer view="panel" showEmptyState />
              </div>
              {isProgressActionItems && actionItemsJsx}
              <div className={`${baseClassName}__section`}>
                <WpButton size="sm" variant="tertiary" color="gray" onClick={toggleIsSubGoalsOpen} square slimPadding>
                  {goal.childrensCount > 0 ? goal.childrensCount : ''} Sub-Goals
                  <WpIcon name={isSubGoalsOpen ? 'angle-down' : 'angle-right'} />
                </WpButton>
                <WpCollapsible open={isSubGoalsOpen}>
                  <div className={`${baseClassName}__section__collapsible-content`}>
                    <GoalDetailsRelatedGoals
                      goal={goal}
                      view="sub-only"
                      collapsible={false}
                      showTitle={false}
                      showSubTitle={false}
                      slimRow
                      onRowClick={handleRowClickOfRelatedGoals}
                      hiddenColumns={[
                        GoalTableColumns.actions,
                        GoalTableColumns.department,
                        GoalTableColumns.owner,
                        GoalTableColumns.progress,
                        GoalTableColumns.status,
                        GoalTableColumns.due,
                        GoalTableColumns.workspace,
                      ]}
                      goalTableComponents={{
                        [GoalTableColumns.name]: GoalCustomTableRow,
                      }}
                    />
                  </div>
                </WpCollapsible>
              </div>
              {!isProgressActionItems && actionItemsJsx}
            </div>
            <div className={`${baseClassName}__comments`}>
              <GoalComments goal={goal} />
            </div>
            <div ref={scrollToRef} />
          </div>
          <div className={`${baseClassName}__comments-input`}>
            <GoalCommentInput goal={goal} scrollToCallback={scrollToCallBack} />
          </div>
        </>
      )}
    </div>
  );
};

export default GoalDetailPanelContent;
